import { toRefs, ref, inject, computed, onMounted } from 'vue';
import { useFlip } from '../../compositions/component-lib.esm11.js';
import { useTheme } from '../../compositions/component-lib.esm7.js';
import { getThemeColor, getThemeAccentColor } from '../../styles/theme/component-lib.esm3.js';

//

/**
 * An accessible list of Tabs.  This is the interactable portion of the
 * tabs component. TabBar should always be wrapped in a Tabs component
 * to provide context, and have one sibling TabPanel for for each of
 * the contained Tabs.
 *
 * Accessibility guidelines were followed from [w3.org](https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html).
 * For example, try tabbing between the tab bar and tab panel, or
 * using the arrow keys to navigate between tabs.
 */
var script = {
  name: 'TabBar',
  props: {
    color: {
      type: String,
      default: 'primary'
    },
    /** Provides a label that describes the purpose of the set of tabs */
    ariaLabel: {
      type: String,
      required: true
    }
  },
  setup(props, _ref) {
    let {
      slots
    } = _ref;
    const {
      color
    } = toRefs(props);
    const theme = useTheme();
    const indexToValues = ref([]);
    const {
      selectedTab
    } = inject('tabs');
    const tabBar = useFlip(selectedTab);
    const styles = computed(() => {
      return {
        '--c-tab-color': getThemeColor(color.value, theme.value),
        '--c-tab-accent-color': getThemeAccentColor({
          color: color.value,
          theme: theme.value
        }, {
          level: 0
        })
      };
    });
    onMounted(() => {
      indexToValues.value = slots.default().filter(tab => tab.componentOptions && tab.componentOptions.tag === 'Tab').reduce((accum, tab) => {
        const val = tab.componentOptions.propsData.value;
        accum.push(val);
        return accum;
      }, []);
    });
    function handleKeydown(e) {
      const currentIndex = indexToValues.value.findIndex(val => val === selectedTab.value);
      const itemCount = indexToValues.value.length;
      switch (e.key) {
        case 'ArrowLeft':
          // eslint-disable-next-line no-case-declarations
          const previousIndex = Math.max(currentIndex - 1, 0);
          selectedTab.value = indexToValues.value[previousIndex];
          break;
        case 'ArrowRight':
          // eslint-disable-next-line no-case-declarations
          const nextIndex = Math.min(currentIndex + 1, itemCount - 1);
          selectedTab.value = indexToValues.value[nextIndex];
          break;
        case 'Home':
          e.preventDefault();
          selectedTab.value = indexToValues.value[0];
          break;
        case 'End':
          e.preventDefault();
          selectedTab.value = indexToValues.value[itemCount - 1];
          break;
      }
    }
    return {
      styles,
      handleKeydown,
      tabBar
    };
  }
};
export { script as default };